import React from 'react';
import { Box, Card, CardContent, Chip, Stack, Tooltip, Typography } from '../../ui';
import { MediaServerStatus, MediaServerType, Playlist } from '../../../types/playlist';
import LibraryDownloadsGroup from './LibraryDownloadsGroup';
import MediaServerSyncGroup from './MediaServerSyncGroup';
import OpenInYouTubeLink, { youtubePlaylistUrl } from '../../shared/OpenInYouTubeLink';
import PlaylistCountsInfo from '../../shared/PlaylistCountsInfo';
import { formatPlaylistCounts } from '../../../utils/playlistCounts';

interface PlaylistHeaderProps {
  playlist: Playlist;
  thumbnailUrl: string;
  isMobile: boolean;
  serverStatus: MediaServerStatus;
  anyConfigured: boolean;
  downloadedCount?: number | null;
  newCount: number | null;
  followingExistingCount?: number | null;
  followingRequestedCount?: number | null;
  onChooseExisting?: () => void;
  togglePending: boolean;
  actionRunning: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  onDownloadAll: () => void;
  onOpenSettings: () => void;
  onToggleAutoDownload: (enabled: boolean) => void;
  onToggleSync: (server: MediaServerType, enabled: boolean) => void;
  onChangePublic: () => void;
  onSyncNow: () => void;
  onRegenerateM3U: () => void;
  unsyncableCount?: number | null;
}

function formatTimestamp(value: string | null): string {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const PlaylistHeader: React.FC<PlaylistHeaderProps> = ({
  playlist,
  thumbnailUrl,
  isMobile,
  serverStatus,
  anyConfigured,
  downloadedCount,
  newCount,
  followingExistingCount,
  followingRequestedCount,
  onChooseExisting,
  togglePending,
  actionRunning,
  refreshing,
  onRefresh,
  onDownloadAll,
  onOpenSettings,
  onToggleAutoDownload,
  onToggleSync,
  onChangePublic,
  onSyncNow,
  onRegenerateM3U,
  unsyncableCount,
}) => {
  const isAudioPlaylist = playlist.audio_format === 'mp3_only';
  return (
    <Card elevation={8} className="mb-4" style={{ borderRadius: 'var(--radius-ui)', overflow: 'hidden' }}>
      <CardContent
        style={{
          paddingLeft: isMobile ? 14 : 16,
          paddingRight: isMobile ? 14 : 16,
          paddingTop: isMobile ? 14 : 16,
          paddingBottom: isMobile ? 14 : 16,
        }}
      >
        <div className="flex flex-col gap-4 md:flex-row md:gap-5">
          <Box
            component="img"
            src={thumbnailUrl}
            alt="Playlist thumbnail"
            className="w-full md:w-[220px] h-[150px] md:h-[124px] object-cover rounded-xl bg-muted block shrink-0"
            style={{ border: '1px solid var(--border)' }}
          />
          <div className="flex flex-col gap-4 flex-1 min-w-0">
            <div>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <Typography variant="h5" className="min-w-0" style={{ fontWeight: 700 }}>
                  {playlist.title}
                </Typography>
                <OpenInYouTubeLink href={youtubePlaylistUrl(playlist.playlist_id)} />
              </div>
              {playlist.uploader && (
                <Typography variant="body2" color="text.secondary">
                  By {playlist.uploader}
                </Typography>
              )}
              <Stack direction="row" spacing={2} className="flex-wrap gap-2 mt-1 items-center">
                <Tooltip
                  title={
                    isAudioPlaylist
                      ? 'This playlist downloads MP3s and syncs to media servers as a music playlist. Set by its Download Type setting.'
                      : 'This playlist syncs to media servers as a video playlist. Set by its Download Type setting.'
                  }
                >
                  <span className="inline-flex">
                    <Chip
                      label={isAudioPlaylist ? 'Audio playlist' : 'Video playlist'}
                      size="small"
                      variant="outlined"
                    />
                  </span>
                </Tooltip>
                <Typography variant="body2" color="text.secondary" className="inline-flex items-center gap-1">
                  {formatPlaylistCounts(playlist.video_count, downloadedCount)}
                  <PlaylistCountsInfo />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last fetched: {formatTimestamp(playlist.lastFetched)}
                </Typography>
              </Stack>
            </div>

            <div className="flex flex-col gap-4 lg:flex-row">
              <LibraryDownloadsGroup
                autoDownload={playlist.auto_download}
                onToggleAutoDownload={onToggleAutoDownload}
                togglePending={togglePending}
                newCount={newCount}
                hasFollowingBaseline={Boolean(playlist.auto_download_baseline_at)}
                setupError={playlist.auto_download_setup_error}
                followingExistingCount={followingExistingCount}
                followingRequestedCount={followingRequestedCount}
                onChooseExisting={onChooseExisting}
                onRefresh={onRefresh}
                onDownloadAll={onDownloadAll}
                onOpenSettings={onOpenSettings}
                actionRunning={actionRunning}
                refreshing={refreshing}
              />
              <MediaServerSyncGroup
                playlist={playlist}
                serverStatus={serverStatus}
                onToggleSync={onToggleSync}
                togglePending={togglePending}
                publicOnServers={playlist.public_on_servers}
                onChangePublic={onChangePublic}
                onSyncNow={onSyncNow}
                onRegenerateM3U={onRegenerateM3U}
                anyConfigured={anyConfigured}
                actionRunning={actionRunning}
                unsyncableCount={unsyncableCount}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PlaylistHeader;
